import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/lib/picker-search";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowLeft, Users, Package, Megaphone, GripVertical, Tag,
  Sparkles, Loader2, Check, FileText, Monitor, Smartphone,
  Shield, RefreshCw, ExternalLink,
} from "lucide-react";
import type { LandingPage, LandingPageSection, Campaign, Persona, Product, Brand } from "@shared/schema";
import LandingPagePreview from "@/components/landing-page-preview";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Reorder } from "framer-motion";

const SECTION_LABELS: Record<string, string> = {
  announcement_bar: "Announcement Bar",
  hero_buy_box: "Hero / Buy Box",
  sticky_buy_bar: "Sticky Buy Bar",
  product_recipe: "Product Cards & Recipe",
  how_to_use: "How to Use",
  science_research: "Science & Research",
  belief_shift_competitor: "Belief Shift / Competitor",
  farmstand_story: "Origin Story",
  timeline_results: "Timeline & Results",
  quiz_funnel: "Quiz Funnel",
  reviews_grid: "Reviews",
  faq_accordion: "FAQ",
  final_cta: "Final CTA",
};

const DEFAULT_SECTIONS = [
  { sectionType: "announcement_bar", order: 0, enabled: true, variant: "default" },
  { sectionType: "hero_buy_box", order: 1, enabled: true, variant: "default" },
  { sectionType: "sticky_buy_bar", order: 2, enabled: true, variant: "default" },
  { sectionType: "product_recipe", order: 3, enabled: true, variant: "default" },
  { sectionType: "how_to_use", order: 4, enabled: true, variant: "default" },
  { sectionType: "science_research", order: 5, enabled: true, variant: "default" },
  { sectionType: "belief_shift_competitor", order: 6, enabled: true, variant: "default" },
  { sectionType: "farmstand_story", order: 7, enabled: true, variant: "default" },
  { sectionType: "timeline_results", order: 8, enabled: true, variant: "default" },
  { sectionType: "quiz_funnel", order: 9, enabled: false, variant: "default" },
  { sectionType: "reviews_grid", order: 10, enabled: true, variant: "default" },
  { sectionType: "faq_accordion", order: 11, enabled: true, variant: "default" },
  { sectionType: "final_cta", order: 12, enabled: true, variant: "default" },
];

export default function LandingPageDetailPage() {
  const { brandId, id } = useParams<{ brandId: string; id: string }>();
  const { toast } = useToast();

  const { data: page, isLoading: pageLoading } = useQuery<LandingPage>({
    queryKey: ["/api/landing-pages", id],
    queryFn: async () => {
      const res = await fetch(`/api/landing-pages/${id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load landing page");
      return res.json();
    },
    enabled: !!id,
  });

  const { data: sections, isLoading: sectionsLoading } = useQuery<LandingPageSection[]>({
    queryKey: ["/api/landing-pages", id, "sections"],
    queryFn: async () => {
      const res = await fetch(`/api/landing-pages/${id}/sections`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load sections");
      return res.json();
    },
    enabled: !!id,
  });

  const { data: brand } = useQuery<Brand>({
    queryKey: ["/api/brands", brandId],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load brand");
      return res.json();
    },
    enabled: !!brandId,
  });

  const { data: campaigns } = useQuery<Campaign[]>({
    queryKey: [`/api/brands/${brandId}/campaigns`],
    enabled: !!brandId,
  });

  const { data: personas } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });

  const { data: products } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });

  const personaMap = new Map(personas?.map(p => [p.id, p]) || []);
  const productMap = new Map(products?.map(p => [p.id, p]) || []);

  useEffect(() => {
    if (sections && sections.length === 0 && id) {
      apiRequest("POST", `/api/landing-pages/${id}/sections`, DEFAULT_SECTIONS).then(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/landing-pages", id, "sections"] });
      });
    }
  }, [sections, id]);

  if (pageLoading) {
    return (
      <div className="p-8 max-w-5xl mx-auto space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[400px] rounded-xl" />
      </div>
    );
  }

  if (!page) {
    return <div className="p-8 max-w-5xl mx-auto text-center py-12 text-muted-foreground">Landing page not found</div>;
  }

  const persona = page.personaId ? personaMap.get(page.personaId) : null;
  const product = page.productId ? productMap.get(page.productId) : null;

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link href={`/brands/${brandId}/landing-pages`}>
          <Button variant="ghost" size="icon" className="shrink-0 rounded-full" data-testid="button-back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold tracking-tight truncate" data-testid="text-page-title">{page.title}</h1>
          <p className="text-xs text-muted-foreground">Landing Page Builder</p>
        </div>
        <Badge className="text-[10px] uppercase tracking-wider">{page.status}</Badge>
      </div>

      <Tabs defaultValue="offer" className="w-full">
        <TabsList className="w-full justify-start" data-testid="tabs-landing-page">
          <TabsTrigger value="offer">Offer Setup</TabsTrigger>
          <TabsTrigger value="sections">Sections</TabsTrigger>
          <TabsTrigger value="ai-draft">AI Draft</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="offer" className="mt-6">
          <OfferSetupTab
            page={page}
            persona={persona || null}
            product={product || null}
            campaigns={campaigns || []}
            brandId={brandId!}
          />
        </TabsContent>

        <TabsContent value="sections" className="mt-6">
          <SectionsTab
            pageId={id!}
            sections={sections || []}
            sectionsLoading={sectionsLoading}
          />
        </TabsContent>

        <TabsContent value="ai-draft" className="mt-6">
          <AiDraftTab
            pageId={id!}
            sections={sections || []}
          />
        </TabsContent>

        <TabsContent value="preview" className="mt-6">
          <PreviewTab
            page={page}
            sections={sections || []}
            persona={persona || null}
            product={product || null}
            brandName={brand?.name || "Brand"}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OfferSetupTab({ page, persona, product, campaigns, brandId }: {
  page: LandingPage;
  persona: Persona | null;
  product: Product | null;
  campaigns: Campaign[];
  brandId: string;
}) {
  const { toast } = useToast();
  const initialOffer = (page.offerData as Record<string, any>) || {};
  const [form, setForm] = useState({
    ctaUrl: initialOffer.ctaUrl || "",
    priceToday: initialOffer.priceToday ?? "",
    retailValue: initialOffer.retailValue ?? "",
    refillPrice: initialOffer.refillPrice ?? "",
    refillCadenceDays: initialOffer.refillCadenceDays ?? "",
    guaranteeDays: initialOffer.guaranteeDays ?? "",
    shippingOffer: initialOffer.shippingOffer || "",
    urgencyLabel: initialOffer.urgencyLabel || "",
  });
  const [importOpen, setImportOpen] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();
  const isInitial = useRef(true);

  const price = parseFloat(form.priceToday) || 0;
  const retail = parseFloat(form.retailValue) || 0;
  const savings = retail > price ? retail - price : 0;
  const discountPct = retail > 0 ? Math.round((savings / retail) * 100) : 0;
  const cadence = parseFloat(form.refillCadenceDays) || 0;
  const perDay = cadence > 0 ? (price / cadence).toFixed(2) : "—";

  const saveOffer = useCallback(async (data: typeof form) => {
    const offerData = {
      ctaUrl: data.ctaUrl,
      priceToday: data.priceToday ? parseFloat(data.priceToday) : null,
      retailValue: data.retailValue ? parseFloat(data.retailValue) : null,
      savingsAmount: savings,
      discountPercent: discountPct,
      refillPrice: data.refillPrice ? parseFloat(data.refillPrice) : null,
      refillCadenceDays: data.refillCadenceDays ? parseInt(data.refillCadenceDays) : null,
      perDayPrice: cadence > 0 ? parseFloat((price / cadence).toFixed(2)) : null,
      guaranteeDays: data.guaranteeDays ? parseInt(data.guaranteeDays) : null,
      shippingOffer: data.shippingOffer,
      urgencyLabel: data.urgencyLabel,
    };
    try {
      await apiRequest("PATCH", `/api/landing-pages/${page.id}`, { offerData });
    } catch {
      // silent
    }
  }, [page.id, savings, discountPct, cadence, price]);

  useEffect(() => {
    if (isInitial.current) { isInitial.current = false; return; }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveOffer(form), 800);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [form, saveOffer]);

  const importMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/landing-pages/${page.id}/import-campaign`, { campaignId: selectedCampaignId });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages", page.id] });
      setImportOpen(false);
      toast({ title: "Campaign imported" });
    },
    onError: (e: any) => {
      toast({ title: "Import failed", description: e.message, variant: "destructive" });
    },
  });

  const update = (key: string, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  return (
    <div className="space-y-5 max-w-2xl">
      {(page.narrativeAngle || page.narrativeOpportunity || persona || product) ? (
        <Card className="rounded-xl border border-border/40 bg-card" data-testid="card-campaign-context">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-5 h-5 rounded-md bg-primary/8 flex items-center justify-center">
                <Megaphone className="h-3 w-3 text-primary" />
              </div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Campaign Context</p>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 pl-7">
              {persona && <span className="inline-flex items-center gap-1.5 text-[12px] text-foreground/70"><Users className="h-3 w-3 text-muted-foreground" /> {persona.name}</span>}
              {product && <span className="inline-flex items-center gap-1.5 text-[12px] text-foreground/70"><Package className="h-3 w-3 text-muted-foreground" /> {product.name}</span>}
            </div>
            {page.narrativeOpportunity && (
              <p className="text-[12px] text-foreground/70 pl-7">
                <span className="text-muted-foreground font-medium">Opportunity:</span> {page.narrativeOpportunity}
              </p>
            )}
            {page.narrativeAngle && (
              <p className="text-[12px] text-foreground/70 pl-7">
                <span className="text-muted-foreground font-medium">Angle:</span> {page.narrativeAngle}
              </p>
            )}
            <div className="pl-7 pt-1">
              <Button variant="ghost" size="sm" className="text-xs h-7 px-2 text-muted-foreground" onClick={() => setImportOpen(true)}>
                <Megaphone className="h-3 w-3 mr-1.5" /> Re-import
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-xl border border-dashed border-border/60 bg-card/50" data-testid="card-no-campaign">
          <CardContent className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-9 h-9 rounded-lg bg-muted/60 flex items-center justify-center mb-3">
              <Megaphone className="h-4 w-4 text-muted-foreground/60" />
            </div>
            <p className="text-sm font-medium text-muted-foreground mb-1">No campaign linked</p>
            <p className="text-xs text-muted-foreground/60 mb-3">Import persona, product, and narrative from an existing campaign</p>
            <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="button-import-campaign">
              <Megaphone className="h-3.5 w-3.5 mr-2" /> Import from Campaign
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-xl border border-border/40 bg-card" data-testid="card-pricing">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-md bg-primary/8 flex items-center justify-center">
              <Tag className="h-3 w-3 text-primary" />
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Pricing & Offer</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-7">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Price Today ($)</Label>
              <Input type="number" value={form.priceToday} onChange={e => update("priceToday", e.target.value)} data-testid="input-price-today" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Retail Value ($)</Label>
              <Input type="number" value={form.retailValue} onChange={e => update("retailValue", e.target.value)} data-testid="input-retail-value" />
            </div>
          </div>
          {(savings > 0 || discountPct > 0) && (
            <div className="flex items-center gap-4 pl-7">
              <div className="flex items-center gap-2 rounded-lg border border-border/40 px-3 py-2">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Savings</span>
                <span className="text-sm font-bold text-foreground" data-testid="text-savings">${savings.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-2 rounded-lg border border-border/40 px-3 py-2">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Discount</span>
                <span className="text-sm font-bold text-foreground" data-testid="text-discount">{discountPct}%</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-xl border border-border/40 bg-card" data-testid="card-subscription">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-md bg-primary/8 flex items-center justify-center">
              <RefreshCw className="h-3 w-3 text-primary" />
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Subscription & Refill</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-7">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Refill Price ($)</Label>
              <Input type="number" value={form.refillPrice} onChange={e => update("refillPrice", e.target.value)} data-testid="input-refill-price" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Refill Cadence (days)</Label>
              <Input type="number" value={form.refillCadenceDays} onChange={e => update("refillCadenceDays", e.target.value)} data-testid="input-refill-cadence" />
            </div>
          </div>
          {perDay !== "—" && (
            <div className="pl-7">
              <div className="inline-flex items-center gap-2 rounded-lg border border-border/40 px-3 py-2">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Per Day</span>
                <span className="text-sm font-bold text-foreground" data-testid="text-per-day">${perDay}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-xl border border-border/40 bg-card" data-testid="card-trust-urgency">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-md bg-primary/8 flex items-center justify-center">
              <Shield className="h-3 w-3 text-primary" />
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Trust & Urgency</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-7">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Guarantee (days)</Label>
              <Input type="number" value={form.guaranteeDays} onChange={e => update("guaranteeDays", e.target.value)} data-testid="input-guarantee" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Shipping Offer</Label>
              <Input value={form.shippingOffer} onChange={e => update("shippingOffer", e.target.value)} placeholder="Free shipping" data-testid="input-shipping" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Urgency Label</Label>
              <Input value={form.urgencyLabel} onChange={e => update("urgencyLabel", e.target.value)} placeholder="Limited time" data-testid="input-urgency" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border border-border/40 bg-card" data-testid="card-cta">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-md bg-primary/8 flex items-center justify-center">
              <ExternalLink className="h-3 w-3 text-primary" />
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">CTA Destination</p>
          </div>
          <div className="pl-7">
            <Input value={form.ctaUrl} onChange={e => update("ctaUrl", e.target.value)} placeholder="https://yourstore.com/checkout" data-testid="input-cta-url" />
          </div>
        </CardContent>
      </Card>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import from Campaign</DialogTitle>
          </DialogHeader>
          <SearchableSelect
            value={selectedCampaignId}
            onValueChange={setSelectedCampaignId}
            placeholder="Select a campaign..."
            testId="select-import-campaign"
            options={campaigns.map(c => ({ value: c.id, label: c.name }))}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button onClick={() => importMutation.mutate()} disabled={!selectedCampaignId || importMutation.isPending}>
              {importMutation.isPending ? "Importing..." : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionsTab({ pageId, sections, sectionsLoading }: {
  pageId: string;
  sections: LandingPageSection[];
  sectionsLoading: boolean;
}) {
  const { toast } = useToast();
  const [orderedSections, setOrderedSections] = useState<LandingPageSection[]>([]);
  const saveTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    if (sections.length > 0) setOrderedSections([...sections]);
  }, [sections]);

  const patchSection = useCallback(async (sectionId: string, data: Record<string, any>) => {
    try {
      await apiRequest("PATCH", `/api/landing-pages/${pageId}/sections/${sectionId}`, data);
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages", pageId, "sections"] });
    } catch {
      // silent
    }
  }, [pageId]);

  const debouncedPatch = useCallback((sectionId: string, data: Record<string, any>) => {
    const existing = saveTimers.current.get(sectionId);
    if (existing) clearTimeout(existing);
    saveTimers.current.set(sectionId, setTimeout(() => {
      patchSection(sectionId, data);
      saveTimers.current.delete(sectionId);
    }, 600));
  }, [patchSection]);

  const handleReorder = (newOrder: LandingPageSection[]) => {
    setOrderedSections(newOrder);
    newOrder.forEach((s, i) => {
      if (s.order !== i) {
        patchSection(s.id, { order: i });
      }
    });
  };

  const handleToggle = (section: LandingPageSection, enabled: boolean) => {
    setOrderedSections(prev => prev.map(s => s.id === section.id ? { ...s, enabled } : s));
    patchSection(section.id, { enabled });
  };

  const handleVariant = (section: LandingPageSection, variant: string) => {
    setOrderedSections(prev => prev.map(s => s.id === section.id ? { ...s, variant } : s));
    patchSection(section.id, { variant });
  };

  const handleOverrideChange = (section: LandingPageSection, field: string, value: string | string[]) => {
    const overrides = { ...(section.overrides as Record<string, any> || {}), [field]: value };
    setOrderedSections(prev => prev.map(s => s.id === section.id ? { ...s, overrides } : s));
    debouncedPatch(section.id, { overrides });
  };

  if (sectionsLoading) return <Skeleton className="h-[300px] rounded-xl" />;

  const enabledSections = orderedSections.filter(s => s.enabled);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-2">
        <h3 className="text-sm font-semibold mb-3">Section Order</h3>
        <Reorder.Group axis="y" values={orderedSections} onReorder={handleReorder} className="space-y-2">
          {orderedSections.map(section => (
            <Reorder.Item key={section.id} value={section} className="cursor-grab active:cursor-grabbing">
              <div className="flex items-center gap-3 p-3 rounded-lg border border-border/40 bg-card" data-testid={`section-row-${section.sectionType}`}>
                <GripVertical className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium">{SECTION_LABELS[section.sectionType] || section.sectionType}</span>
                </div>
                <Select value={section.variant || "default"} onValueChange={v => handleVariant(section, v)}>
                  <SelectTrigger className="w-24 h-7 text-[11px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default</SelectItem>
                    <SelectItem value="minimal">Minimal</SelectItem>
                  </SelectContent>
                </Select>
                <Switch
                  checked={section.enabled}
                  onCheckedChange={v => handleToggle(section, v)}
                  data-testid={`switch-${section.sectionType}`}
                />
              </div>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-3">Section Content</h3>
        {enabledSections.length === 0 ? (
          <p className="text-sm text-muted-foreground">Enable sections to edit their content</p>
        ) : (
          <Accordion type="multiple" className="space-y-2">
            {enabledSections.map(section => {
              const ov = (section.overrides as Record<string, any>) || {};
              return (
                <AccordionItem key={section.id} value={section.id} className="border border-border/40 rounded-lg px-4">
                  <AccordionTrigger className="text-sm font-medium py-3" data-testid={`accordion-${section.sectionType}`}>
                    {SECTION_LABELS[section.sectionType] || section.sectionType}
                  </AccordionTrigger>
                  <AccordionContent className="space-y-3 pb-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Headline</Label>
                      <Input
                        value={ov.headline || ""}
                        onChange={e => handleOverrideChange(section, "headline", e.target.value)}
                        placeholder="Section headline..."
                        data-testid={`input-headline-${section.sectionType}`}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Subheadline</Label>
                      <Input
                        value={ov.subheadline || ""}
                        onChange={e => handleOverrideChange(section, "subheadline", e.target.value)}
                        placeholder="Subheadline..."
                        data-testid={`input-subheadline-${section.sectionType}`}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Body</Label>
                      <Textarea
                        value={ov.body || ""}
                        onChange={e => handleOverrideChange(section, "body", e.target.value)}
                        placeholder="Body copy..."
                        rows={3}
                        data-testid={`textarea-body-${section.sectionType}`}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">CTA Text</Label>
                        <Input
                          value={ov.ctaText || ""}
                          onChange={e => handleOverrideChange(section, "ctaText", e.target.value)}
                          placeholder="Buy Now"
                          data-testid={`input-cta-${section.sectionType}`}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">CTA URL</Label>
                        <Input
                          value={ov.ctaUrl || ""}
                          onChange={e => handleOverrideChange(section, "ctaUrl", e.target.value)}
                          placeholder="https://..."
                          data-testid={`input-cta-url-${section.sectionType}`}
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Bullets (one per line)</Label>
                      <Textarea
                        value={Array.isArray(ov.bullets) ? ov.bullets.join("\n") : (ov.bullets || "")}
                        onChange={e => handleOverrideChange(section, "bullets", e.target.value.split("\n"))}
                        placeholder="One bullet per line..."
                        rows={3}
                        data-testid={`textarea-bullets-${section.sectionType}`}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>
    </div>
  );
}

function AiDraftTab({ pageId, sections }: { pageId: string; sections: LandingPageSection[] }) {
  const { toast } = useToast();
  const [generating, setGenerating] = useState<Set<string>>(new Set());

  const enabledSections = sections.filter(s => s.enabled);

  const generateDraft = async (section: LandingPageSection) => {
    setGenerating(prev => new Set(prev).add(section.id));
    try {
      await apiRequest("POST", `/api/landing-pages/${pageId}/sections/${section.id}/ai-draft`);
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages", pageId, "sections"] });
      toast({ title: `Draft generated for ${SECTION_LABELS[section.sectionType] || section.sectionType}` });
    } catch (e: any) {
      const msg = e.message || "Generation failed";
      toast({ title: "AI draft failed", description: msg, variant: "destructive" });
    } finally {
      setGenerating(prev => {
        const next = new Set(prev);
        next.delete(section.id);
        return next;
      });
    }
  };

  return (
    <div className="space-y-3 max-w-2xl">
      <p className="text-sm text-muted-foreground mb-4">
        Generate AI-written copy for each section. Make sure Offer Setup and Campaign Context are filled in for best results.
      </p>
      {enabledSections.length === 0 ? (
        <p className="text-sm text-muted-foreground">No enabled sections. Enable sections in the Sections tab first.</p>
      ) : (
        enabledSections.map(section => {
          const ov = (section.overrides as Record<string, any>) || {};
          const isDrafted = !!(ov.headline || ov.body);
          const isGenerating = generating.has(section.id);
          return (
            <div
              key={section.id}
              className="flex items-center gap-3 p-3 rounded-lg border border-border/40 bg-card"
              data-testid={`ai-row-${section.sectionType}`}
            >
              <FileText className="h-4 w-4 text-muted-foreground/50 shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium">{SECTION_LABELS[section.sectionType] || section.sectionType}</span>
                {ov.headline && (
                  <p className="text-[11px] text-muted-foreground italic truncate mt-0.5">{ov.headline}</p>
                )}
              </div>
              <Badge
                variant="secondary"
                className={`text-[10px] shrink-0 ${isDrafted ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300" : ""}`}
              >
                {isDrafted ? "Drafted" : "Not drafted"}
              </Badge>
              <Button
                size="sm"
                variant="outline"
                className="text-xs h-7 shrink-0"
                onClick={() => generateDraft(section)}
                disabled={isGenerating}
                data-testid={`button-generate-${section.sectionType}`}
              >
                {isGenerating ? (
                  <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Generating</>
                ) : isDrafted ? (
                  <><Sparkles className="h-3 w-3 mr-1.5" /> Regenerate</>
                ) : (
                  <><Sparkles className="h-3 w-3 mr-1.5" /> Generate Draft</>
                )}
              </Button>
            </div>
          );
        })
      )}
    </div>
  );
}

function PreviewTab({ page, sections, persona, product, brandName }: {
  page: LandingPage;
  sections: LandingPageSection[];
  persona: Persona | null;
  product: Product | null;
  brandName: string;
}) {
  const [viewMode, setViewMode] = useState<"desktop" | "mobile">("desktop");
  const containerWidth = viewMode === "desktop" ? "100%" : "390px";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Live preview of your landing page layout and copy.
        </p>
        <div className="flex items-center gap-1 border border-border/40 rounded-lg p-0.5">
          <Button
            variant={viewMode === "desktop" ? "secondary" : "ghost"}
            size="sm"
            className="h-7 px-2.5"
            onClick={() => setViewMode("desktop")}
            data-testid="button-preview-desktop"
          >
            <Monitor className="h-3.5 w-3.5 mr-1.5" />
            Desktop
          </Button>
          <Button
            variant={viewMode === "mobile" ? "secondary" : "ghost"}
            size="sm"
            className="h-7 px-2.5"
            onClick={() => setViewMode("mobile")}
            data-testid="button-preview-mobile"
          >
            <Smartphone className="h-3.5 w-3.5 mr-1.5" />
            Mobile
          </Button>
        </div>
      </div>
      <div
        className="mx-auto border border-border/40 rounded-xl overflow-hidden shadow-sm bg-white"
        style={{ width: containerWidth, maxWidth: "100%", maxHeight: "70vh", overflowY: "auto" }}
        data-testid="preview-container"
      >
        <LandingPagePreview
          page={page}
          sections={sections}
          persona={persona}
          product={product}
          brandName={brandName}
        />
      </div>
    </div>
  );
}
